import { isServiceCategoryId } from '@powerful/config/categories';
import { z } from 'zod';

const planCode = z.enum([
  'essencial',
  'profissional',
  'premium',
  'powerful_partner',
  'free',
  'connect_plus',
  'connect_plus_premium',
]);

const serviceDraftSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    duration_minutes: z.number().int().positive().optional(),
  })
  .strict();

const professionalDraftSchema = z
  .object({
    full_name: z.string().trim().min(1).max(120),
    email: z.string().email().optional(),
  })
  .strict();

/**
 * Contratos do onboarding profissional aprovado no M1. A etapa de pagamento não existe aqui:
 * ela saiu do fluxo, que tem sete etapas, e o estado do Stripe nunca vem do cliente.
 */
export const businessStepSchemas = [
  z
    .object({
      name: z.string().trim().min(1).max(120),
      // T2.2: FK para service_categories — recusar aqui evita um 23503 cru no cliente.
      primary_category_id: z
        .string()
        .uuid()
        .refine(isServiceCategoryId, { message: 'Categoria desconhecida.' })
        .optional(),
      nif: z.string().trim().min(1).max(32).optional(),
    })
    .strict(),
  z.object({ services: z.array(serviceDraftSchema).default([]) }).strict(),
  z
    .object({
      address_line: z.string().trim().min(1).max(200).optional(),
      city: z.string().trim().min(1).max(120).optional(),
      postal_code: z.string().trim().min(1).max(20).optional(),
      photo_urls: z.array(z.string().url()).default([]),
      amenities: z.array(z.string().trim().min(1).max(80)).default([]),
    })
    .strict(),
  z
    .object({
      business_hours: z.array(z.record(z.unknown())).default([]),
      deposit_cents: z.number().int().nonnegative().optional(),
      minimum_notice_hours: z.number().int().nonnegative().optional(),
    })
    .strict(),
  z
    .object({
      professionals: z.array(professionalDraftSchema).default([]),
      current_plan: planCode.optional(),
    })
    .strict(),
  z
    .object({
      legal_name: z.string().trim().min(1).max(160).optional(),
      nif: z.string().trim().min(1).max(32).optional(),
      payment_account_reference: z.string().trim().min(1).max(160).optional(),
    })
    .strict(),
  z.object({ acknowledged: z.boolean().default(false) }).strict(),
] as const;

export type BusinessStepInput = z.input<(typeof businessStepSchemas)[number]>;

/**
 * Estado do KYC Stripe Connect Express, como o cliente o lê (T1.15).
 *
 * 🔴 É um contrato de **leitura**. O cliente nunca envia estes campos: `charges_enabled` é o que
 * liberta o botão "Agendar" (PRD §5.2.1), e a tabela não lhe concede privilégio de escrita —
 * quem se verifica não decide que está verificado. O valor chega pelo webhook `account.updated`.
 */
export const connectAccountStateSchema = z
  .object({
    stripe_account_id: z.string().trim().min(1),
    charges_enabled: z.boolean(),
    payouts_enabled: z.boolean(),
    details_submitted: z.boolean(),
    requirements: z.record(z.unknown()).default({}),
    onboarding_started_at: z.string().datetime({ offset: true }),
    charges_enabled_at: z.string().datetime({ offset: true }).nullable(),
  })
  .strict()
  // `charges_enabled` e `charges_enabled_at` contam a mesma história; a base tem a mesma
  // constraint. Divergirem aqui significaria ler um estado que o banco não permite existir.
  .refine((estado) => estado.charges_enabled === (estado.charges_enabled_at !== null), {
    message: 'charges_enabled e charges_enabled_at têm de concordar.',
  });

export type ConnectAccountState = z.output<typeof connectAccountStateSchema>;

/**
 * Tipo de estabelecimento (T2.3 / ADR 0044): a identidade principal do ESPAÇO, não a categoria do
 * serviço. `studio` inclui espaço próprio na residência do profissional. Não há `at_home`, e nulo é
 * legítimo (consultório/gabinete sem tipo fiel) — nunca inferir da categoria.
 */
export const establishmentTypes = ['salon', 'barbershop', 'studio', 'clinic', 'spa'] as const;

export const establishmentTypeSchema = z.enum(establishmentTypes);

export type EstablishmentType = z.output<typeof establishmentTypeSchema>;

/** Idiomas falados: ISO 639-1 minúsculo, 0–12, sem duplicatas. A ordem é a do dono. */
export const languageCodesSchema = z
  .array(z.string().regex(/^[a-z]{2}$/, 'Código ISO 639-1 de duas letras minúsculas.'))
  .max(12)
  .refine((codigos) => new Set(codigos).size === codigos.length, { message: 'Idioma repetido.' });

/** O que o dono edita (UI na T4.9). `booking_enabled`/`last_booking_confirmed_at` são do servidor. */
export const businessDiscoveryUpdateSchema = z
  .object({
    languages: languageCodesSchema.optional(),
    establishment_type: establishmentTypeSchema.nullable().optional(),
  })
  .strict();

/**
 * 🔴 Contrato de **leitura** (negócio e projeção pública). `booking_enabled` espelha o
 * `charges_enabled` do Stripe e não significa horário disponível; `last_booking_confirmed_at`
 * fica nulo até a T3.5.
 */
export const businessDiscoveryReadSchema = z
  .object({
    languages: languageCodesSchema,
    establishment_type: establishmentTypeSchema.nullable(),
    booking_enabled: z.boolean(),
    last_booking_confirmed_at: z.string().datetime({ offset: true }).nullable(),
  })
  .strict();

export type BusinessDiscovery = z.output<typeof businessDiscoveryReadSchema>;

/** Motivos de promoção a `professional_owner`. Ver ADR 0037. */
export const promotionReasons = [
  // M1: a promoção ocorre ao iniciar o Connect Express, sem pagamento.
  'kyc_started_m1',
  // M5: o gatilho canónico do PRD §7.12.1 volta com o Stripe Billing.
  'subscription_active',
  'subscription_trialing',
] as const;

export type PromotionReason = (typeof promotionReasons)[number];
